/**
 * Billing Store - Zustand store for subscription management
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  getSubscriptionStatus,
  createSubscription,
  cancelSubscription,
  syncCustomerProfile,
  SubscriptionStatus,
  SubscriptionResponse,
} from '../api/subscriptions';

/**
 * Helper to safely extract error message from API response
 * Handles both string and object error formats: {error_code, message}
 */
const extractErrorMessage = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail?.message && typeof detail.message === 'string') {
    return detail.message;
  }
  return fallback;
};

interface BillingState {
  // Subscription data
  subscription: SubscriptionStatus | null;

  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isCancelling: boolean;
  isSyncing: boolean;

  // Error state
  error: string | null;

  // Actions
  loadSubscription: () => Promise<void>;
  createNewSubscription: (tierCode: string, isRecurring: boolean) => Promise<SubscriptionResponse>;
  cancelCurrentSubscription: () => Promise<SubscriptionResponse>;
  syncProfile: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  subscription: null,
  isLoading: false,
  isCreating: false,
  isCancelling: false,
  isSyncing: false,
  error: null,
};

export const useBillingStore = create<BillingState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Load current subscription status
       */
      loadSubscription: async () => {
        set({ isLoading: true, error: null });

        try {
          const status = await getSubscriptionStatus();
          set({ subscription: status, isLoading: false });
        } catch (error: any) {
          const errorMessage = extractErrorMessage(error, 'Failed to load subscription status');
          set({ error: errorMessage, isLoading: false });
        }
      },

      /**
       * Create a new subscription (recurring or one-time)
       */
      createNewSubscription: async (tierCode: string, isRecurring: boolean = true) => {
        set({ isCreating: true, error: null });

        try {
          const response = await createSubscription(tierCode, isRecurring);

          // Reload subscription status after creation
          await get().loadSubscription();

          set({ isCreating: false });
          return response;
        } catch (error: any) {
          const errorMessage = extractErrorMessage(error, 'Failed to create subscription');
          set({ error: errorMessage, isCreating: false });
        }
      },

      /**
       * Cancel current subscription
       */
      cancelCurrentSubscription: async () => {
        set({ isCancelling: true, error: null });

        try {
          const response = await cancelSubscription();

          // Reload subscription status after cancellation
          await get().loadSubscription();

          set({ isCancelling: false });
          return response;
        } catch (error: any) {
          const errorMessage = extractErrorMessage(error, 'Failed to cancel subscription');
          set({ error: errorMessage, isCancelling: false });
        }
      },

      /**
       * Sync customer profile to ChargeKeep
       */
      syncProfile: async () => {
        set({ isSyncing: true, error: null });

        try {
          await syncCustomerProfile();

          // Reload subscription after sync
          await get().loadSubscription();

          set({ isSyncing: false });
        } catch (error: any) {
          const errorMessage = extractErrorMessage(error, 'Failed to sync customer profile');
          set({ error: errorMessage, isSyncing: false });
        }
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set(initialState);
      },
    }),
    { name: 'BillingStore' }
  )
);

/**
 * Computed selectors for common subscription states
 */
export const useBillingSelectors = () => {
  const subscription = useBillingStore((state) => state.subscription);

  return {
    // Status checks
    isActive: subscription?.status === 'active',
    isInactive: subscription?.status === 'inactive',
    isExpired: subscription?.status === 'expired',
    isCanceled: subscription?.status === 'canceled',
    isPastDue: subscription?.status === 'past_due',
    isTrialing: subscription?.status === 'trialing',
    isInGracePeriod: subscription?.is_grace_period || false,

    // Access information
    hasAccess: subscription?.status === 'active' || subscription?.is_grace_period || subscription?.status === 'trialing',
    daysRemaining: subscription?.days_remaining ?? null,
    graceDaysRemaining: subscription?.grace_days_remaining ?? 0,

    // Subscription type
    isRecurring: subscription?.subscription_type === 'recurring',
    isOneTime: subscription?.subscription_type === 'one_time',

    // Tier information
    currentTier: subscription?.subscription_tier || 'free',

    // Date information
    accessExpiresAt: subscription?.access_expires_at ? new Date(subscription.access_expires_at) : null,
    gracePeriodEndsAt: subscription?.grace_period_ends_at ? new Date(subscription.grace_period_ends_at) : null,
    lastPaymentDate: subscription?.last_payment_date ? new Date(subscription.last_payment_date) : null,
    paymentFailedAt: subscription?.payment_failed_at ? new Date(subscription.payment_failed_at) : null,

    // ChargeKeep IDs removed from client-side state for security
    // These should only be used server-side and never exposed to the client
  };
};

/**
 * Hook to get urgency level for grace period warnings
 */
export const useGracePeriodUrgency = (): 'warning' | 'urgent' | 'critical' | null => {
  const { isInGracePeriod, graceDaysRemaining } = useBillingSelectors();

  if (!isInGracePeriod) return null;

  if (graceDaysRemaining >= 4) return 'warning';
  if (graceDaysRemaining >= 1) return 'urgent';
  return 'critical';
};

/**
 * Hook to get grace period message
 */
export const useGracePeriodMessage = (): string | null => {
  const { isInGracePeriod, graceDaysRemaining } = useBillingSelectors();
  const urgency = useGracePeriodUrgency();

  if (!isInGracePeriod || !urgency) return null;

  const daysText = graceDaysRemaining === 1 ? 'day' : 'days';

  switch (urgency) {
    case 'warning':
      return `Your payment failed. You have ${graceDaysRemaining} ${daysText} of grace period remaining with read-only access.`;
    case 'urgent':
      return `⚠️ Payment required soon! Only ${graceDaysRemaining} ${daysText} remaining before access expires.`;
    case 'critical':
      return `🚨 URGENT: Your access expires in ${graceDaysRemaining} ${daysText}! Update payment now to avoid losing access.`;
    default:
      return null;
  }
};
